import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Server, Trash2, Mail, Lock, Eye } from 'lucide-react';

const PolitiqueConfidentialitePage = () => {
  return (
    <MainLayout>
      <div className="container mx-auto py-8 px-4">
        <h1 className="text-3xl font-bold text-center mb-8">Politique de Confidentialité</h1>
        
        <Card className="max-w-4xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Protection de vos données personnelles
            </CardTitle>
            <p className="text-sm text-muted-foreground">Dernière mise à jour : 26 mars 2026</p>
          </CardHeader>
          <CardContent className="space-y-8">

            {/* Responsable du traitement */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4 text-primary" />
                1. Responsable du traitement
              </h2>
              <p className="text-muted-foreground">
                Le responsable du traitement des données personnelles est NautiCoach. 
                Pour toute question relative à la protection de vos données, vous pouvez nous contacter à l'adresse :
              </p>
              <p className="font-medium mt-2 text-primary">rgpd@nauticoach.com</p>
            </section>

            {/* Données collectées */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Eye className="h-4 w-4 text-primary" />
                2. Données collectées
              </h2>
              <p className="text-muted-foreground mb-2">
                Nous collectons les données que vous nous fournissez lors de votre inscription et de l'utilisation de nos services :
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li><strong>Données d'identification :</strong> nom, prénom, adresse e-mail, téléphone</li>
                <li><strong>Données du navire :</strong> nom, immatriculation, numéro HIN, MMSI, port d'attache</li>
                <li><strong>Données d'utilisation :</strong> inventaire, maintenances, checklists, documents embarqués</li>
                <li><strong>Données de navigation :</strong> pages visitées, durée de session (anonymisées)</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3 bg-muted/50 p-3 rounded-lg">
                ⚠️ Les données d'identification du navire (HIN, MMSI, immatriculation) sont considérées comme des données à caractère personnel au sens du RGPD. 
                Elles ne sont jamais exposées dans les journaux techniques ni transmises à des tiers.
              </p>
            </section>

            {/* Finalités */}
            <section>
              <h2 className="text-xl font-semibold mb-3">3. Finalités du traitement</h2>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li>Fournir et améliorer les services NautiCoach</li>
                <li>Gérer votre compte et votre abonnement</li>
                <li>Vous envoyer des notifications de maintenance</li>
                <li>Assurer la sécurité et le bon fonctionnement de la plateforme</li>
                <li>Respecter nos obligations légales</li>
              </ul>
              <p className="text-muted-foreground mt-2">
                <strong>Base légale :</strong> exécution du contrat (CGU/CGV), consentement, et intérêt légitime.
              </p>
            </section>

            {/* Hébergement */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Server className="h-4 w-4 text-primary" />
                4. Hébergement et sécurité des données
              </h2>
              <p className="text-muted-foreground">
                Vos données sont hébergées <strong>en Europe (Union Européenne)</strong> via l'infrastructure Supabase, 
                elle-même basée sur Amazon Web Services (AWS) dans la région <strong>eu-west (Irlande/Francfort)</strong>.
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-3 space-y-1 ml-2">
                <li><strong>Chiffrement en transit :</strong> TLS/SSL sur toutes les connexions</li>
                <li><strong>Chiffrement au repos :</strong> AES-256 au niveau disque</li>
                <li><strong>Mots de passe :</strong> hachés via bcrypt, jamais stockés en clair</li>
                <li><strong>Accès aux données :</strong> isolement par utilisateur via Row-Level Security (RLS)</li>
                <li><strong>Documents :</strong> stockés dans un bucket privé avec accès authentifié uniquement</li>
              </ul>
            </section>

            {/* Partage */}
            <section>
              <h2 className="text-xl font-semibold mb-3">5. Partage des données</h2>
              <p className="text-muted-foreground">
                Nous ne vendons, n'échangeons et ne transférons <strong>jamais</strong> vos données personnelles à des tiers 
                à des fins commerciales. Vos données peuvent être partagées uniquement avec :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1 ml-2">
                <li><strong>Supabase :</strong> hébergement des données (sous-traitant, conforme RGPD)</li>
                <li><strong>Stripe :</strong> traitement des paiements (certifié PCI-DSS)</li>
                <li><strong>Perplexity / OpenRouter :</strong> traitement des requêtes IA. Aucune donnée personnelle n'est transmise automatiquement aux services d'IA. Seul le contenu des questions posées par l'Utilisateur est transmis. L'Utilisateur est invité à ne pas saisir d'informations nominatives dans ses requêtes à l'assistant.</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3 bg-muted/50 p-3 rounded-lg">
                En cas de changement de sous-traitant, NautiCoach s'engage à maintenir un niveau de protection équivalent et à en informer les utilisateurs.
              </p>
            </section>

            {/* Durée de conservation */}
            <section>
              <h2 className="text-xl font-semibold mb-3">6. Durée de conservation</h2>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li><strong>Données de compte :</strong> conservées tant que le compte est actif</li>
                <li><strong>Données de paiement :</strong> conservées 10 ans (obligation comptable)</li>
                <li><strong>Données de navigation :</strong> 13 mois maximum</li>
                <li><strong>Après suppression du compte :</strong> toutes les données sont effacées sous 30 jours</li>
              </ul>
            </section>

            {/* Vos droits */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Lock className="h-4 w-4 text-primary" />
                7. Vos droits (RGPD)
              </h2>
              <p className="text-muted-foreground mb-2">
                Conformément au Règlement Général sur la Protection des Données (UE 2016/679), vous disposez des droits suivants :
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-2">
                <li><strong>Droit d'accès :</strong> obtenir une copie de vos données personnelles</li>
                <li><strong>Droit de rectification :</strong> corriger des données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> demander la suppression de vos données</li>
                <li><strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré (CSV ou JSON). Pour exercer ce droit, adressez votre demande à rgpd@nauticoach.com ; un export vous sera transmis par e-mail sous 30 jours.</li>
                <li><strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</li>
                <li><strong>Droit à la limitation :</strong> limiter le traitement dans certains cas</li>
              </ul>
            </section>

            {/* Suppression de compte */}
            <section>
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-destructive" />
                8. Suppression de compte et droit à l'oubli
              </h2>
              <p className="text-muted-foreground">
                Vous pouvez supprimer votre compte à tout moment depuis la page <strong>"Mon compte"</strong> de l'application. 
                Cette action entraîne la suppression immédiate et irréversible de :
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1 ml-2">
                <li>Votre profil utilisateur</li>
                <li>Tous vos bateaux et leurs documents associés</li>
                <li>Votre inventaire, maintenances, checklists</li>
                <li>Vos fichiers stockés (photos, documents)</li>
                <li>Votre historique de questions à l'assistant IA</li>
              </ul>
              <p className="text-muted-foreground mt-3">
                Vous pouvez également demander la suppression de vos données par e-mail à :
              </p>
              <p className="font-medium mt-1 text-primary">rgpd@nauticoach.com</p>
            </section>

            {/* Cookies */}
            <section>
              <h2 className="text-xl font-semibold mb-3">9. Cookies</h2>
              <p className="text-muted-foreground">
                NautiCoach utilise uniquement des cookies techniques essentiels au fonctionnement du service 
                (authentification, préférences locales). Aucun cookie publicitaire ou de tracking tiers n'est utilisé.
              </p>
            </section>

            {/* Contact */}
            <section className="bg-muted/30 p-6 rounded-lg">
              <h2 className="text-xl font-semibold mb-3">10. Contact et réclamation</h2>
              <p className="text-muted-foreground">
                Pour exercer vos droits ou pour toute question relative à cette politique :
              </p>
              <p className="font-medium mt-2">📧 rgpd@nauticoach.com</p>
              <p className="text-muted-foreground mt-3 text-sm">
                En cas de différend, vous pouvez introduire une réclamation auprès de la CNIL 
                (Commission Nationale de l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary underline">www.cnil.fr</a>
              </p>
            </section>

          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default PolitiqueConfidentialitePage;
